import React, { useState, useRef, FormEvent } from 'react';
import { Input, Button, Space, Tooltip, Form } from 'antd';
import { SendOutlined, StopOutlined } from '@ant-design/icons';

const { TextArea } = Input;

export interface ChatInputProps {
  onSend: (message: string) => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  onStop?: () => void;
  showStopButton?: boolean;
  autoFocus?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  loading = false,
  disabled = false,
  placeholder = "Type your message here...",
  maxLength = 4000,
  onStop,
  showStopButton = false,
  autoFocus = true,
}) => {
  const [message, setMessage] = useState('');
  const textAreaRef = useRef<any>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmedMessage = message.trim();
    
    if (trimmedMessage && !loading && !disabled) {
      onSend(trimmedMessage);
      setMessage('');
      
      // Focus back to input after sending
      setTimeout(() => {
        textAreaRef.current?.focus();
      }, 100);
    }
  };



  const handleStop = () => {
    if (onStop) {
      onStop();
    }
  };

  const isMessageValid = message.trim().length > 0;
  const canSend = isMessageValid && !loading && !disabled;

  return (
    <div style={{
      padding: '16px',
      // borderTop: '1px solid #f0f0f0',
      // backgroundColor: '#fafafa',
    }}>
      <form onSubmit={handleSubmit}>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            ref={textAreaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            maxLength={maxLength}
            style={{
              resize: 'none',
              borderRadius: '8px 0 0 8px',
            }}
            showCount={{
              formatter: ({ count, maxLength }) => 
                maxLength ? `${count}/${maxLength}` : `${count}`,
            }}
          />
          
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {showStopButton && loading ? (
              <Tooltip title="Stop generation">
                <Button
                  type="primary"
                  danger
                  icon={<StopOutlined />}
                  onClick={handleStop}
                  style={{
                    height: 'auto',
                    minHeight: 32,
                    borderRadius: '0 8px 8px 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              </Tooltip>
            ) : (
              <Tooltip title={canSend ? "Send message (Enter)" : "Enter a message to send"}>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  htmlType="submit"
                  loading={loading}
                  disabled={!canSend}
                  style={{
                    height: 'auto',
                    minHeight: 32,
                    borderRadius: '0 8px 8px 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              </Tooltip>
            )}
          </div>
        </Space.Compact>
      </form>
      
      {/* Helper text */}
      <div style={{
        marginTop: 8,
        fontSize: 12,
        color: '#8c8c8c',
        textAlign: 'center',
      }}>
        Press <strong>Enter</strong> to send, <strong>Shift + Enter</strong> for new line
      </div>
    </div>
  );
};

export default ChatInput;
